<template>
<div class="home-page" :class="themeClass" ref="homePageRef" :style="{ backgroundColor: themeBgColor }">
  
  <!-- 👇 背景图直接加上 z-index: -1，死死压在底层，绝对出不来！ -->
  <img
    class="footprint-bg-img"
    :class="{ 'bg-loaded': bgImgReady }"
    :src="currentBg"
    alt=""
    
  />

  <div class="layout-inner">
  
    <div class="title-wrap" ref="titleRef">
      <div class="title-inner">
        <img src="@/assets/title-logo.webp" class="art-title-img" alt="山河印记 · 情满中国" />
     </div>
    </div>
    
    <div class="map-box" ref="mapBoxRef">
      <FootprintMap
        :footData="realFootData"
        :isSummaryView="currentView==='summary'"
        :groupType="groupType"
        :isLogin="isLogin"
        @province-click="handleMapClick"
      />
      <div class="legend" v-if="isLogin"></div>
    </div>

    <!-- ================= 未登录状态 ================= -->
    <div class="login-wrap" v-if="!isLogin">
      <div class="btn-bottom">
        <div class="help-icon" @click="$router.push('/help')">ℹ️</div>
        <button class="login-btn login-outline" @click="$router.push('/login')">
          <div class="btn-text-wrap">
            <span>登录/注册</span>
          </div>
        </button>
      </div>
    </div>
    
    <!-- ================= 已登录状态 ================= -->
    <div class="bottom-action-wrap" ref="bottomRef" v-else>
      <span class="role-text-line" v-if="currentView !== 'summary'">
        当前角色：{{roleName}}
      </span>
      <div class="btn-bottom">
        <div class="help-icon" @click="$router.push('/help')">ℹ️</div>
        <template>
          <button class="login-btn" @click="openRolePopup"><span>角色切换</span></button>
          <button class="login-btn" @click="clickMarkSummary"><span>印记汇总</span></button>
          <button class="login-btn" @click="$router.push('/personal-center')"><span>个人中心</span></button>
          <button class="login-btn logout-btn" @click="handleLogout"><span>退出登录</span></button>
        </template>
      </div>
    </div>

    <!-- 👇 版权盒子 -->
    <div class="copyright-box">
      <span>©2026 爱的印记 </span>
      <a href="https://beian.miit.gov.cn/" target="_blank">渝ICP备2026026526号</a>
    </div>

  </div> <!-- layout-inner 结束 -->

  <!-- ================= 角色切换弹窗 ================= -->
  <div v-if="showRolePopup" class="popup-mask" @click="showRolePopup=false">
    <div class="popup-panel" @click.stop>
      <div class="popup-title">选择角色</div>
      
      <div class="role-list-wrap">
        <div class="pop-type-row" @click="selectRole('personal', '个人')">
          <span class="type-dot"></span>
          <span class="type-title-text">个人</span>
        </div>
        
        <div
          class="pop-type-row"
          v-for="typeItem in groupTreeData"
          :key="typeItem.typeKey"
        >
          <span class="type-dot"></span>
          <span class="type-title-text">{{ typeItem.typeLabel }}</span>
          <div class="type-inline-groups">
            <span
              class="pop-inline-child-item"
              v-for="g in typeItem.groups"
              :key="g.groupId"
              @click.stop="selectRole(g.groupId, g.groupName, typeItem.rawGroupType, g.otherSubType)"
            >
              {{g.groupName}}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

</div>
</template>

<script>
import FootprintMap from '@/components/FootprintMap.vue'
import bgLover from "@/assets/bg-lover.webp"
import bgFamily from "@/assets/bg-family.webp"
import bgElder from "@/assets/bg-elder.webp"
import bgOther from "@/assets/other-bg.webp"
import bgPersonal from "@/assets/bg-personal.webp"

// 👇 引入 4:3 方图
import bgLoverSquare from "@/assets/bg-lover-square.webp"
import bgFamilySquare from "@/assets/bg-family-square.webp"
import bgElderSquare from "@/assets/bg-elder-square.webp"
import bgOtherSquare from "@/assets/other-bg-square.webp"
import bgPersonalSquare from "@/assets/bg-personal-square.webp"

import { getCurrentUser, setCurrentUser, getGroupList, getFootprintList } from '@/api/footprint'

export default {
  name: 'Index',
  components: {
    FootprintMap
  },
  data() {
    // 👇 核心修正：绝对精确判断！
    // 只有屏幕宽度大于 768px 且 宽度小于高度乘 1.6 （比如 iPad 的 1024x768 比例是 1.33，会触发）
    // 手机横屏（比如 844x390）比例是 2.16，大于 1.6，绝对不会触发！
    const isSquareScreen = window.innerWidth >= 768 && (window.innerWidth / window.innerHeight) < 1.6;

    const cached = JSON.parse(localStorage.getItem('currentUser') || '{}')
    const cachedGroupType = cached.groupType || 'lover'
    
    const bgMap = {
      lover: isSquareScreen ? bgLoverSquare : bgLover,
      parent: isSquareScreen ? bgElderSquare : bgElder,
      child: isSquareScreen ? bgFamilySquare : bgFamily,
      other: isSquareScreen ? bgOtherSquare : bgOther,
      personal: isSquareScreen ? bgPersonalSquare : bgPersonal
    }

    return {
      mapBoxHeight: '400px',
      footList: [],
      footData: [],
      groupType: cachedGroupType,
      isLogin: false,
      randomBg: '',
      realFootData: [],
      showRolePopup: false,
      groupTreeData: [],
      roleName: "个人",
      currentView: 'summary',
      lightProvinceSet: new Set(),
      resizeTimer: null,
      currentBg: bgMap[cachedGroupType] || (isSquareScreen ? bgLoverSquare : bgLover),
      bgImgReady: true,
      isSquareScreen: isSquareScreen 
    }
  },

  computed: {
    themeClass() {
      if (this.groupType === 'lover') return 'theme-lover'
      if (this.groupType === 'parent') return 'theme-elder'
      if (this.groupType === 'child') return 'theme-family'
      if (this.groupType === 'other') return 'theme-other'
      if (this.groupType === 'personal') return 'theme-personal'
      return 'theme-lover'
    },
    themeColor() {
      if (this.groupType === 'lover') return '#ff748c'
      if (this.groupType === 'parent') return '#32b070'
      if (this.groupType === 'child') return '#4298f5'
      if (this.groupType === 'other') return '#8866dd'
      if (this.groupType === 'personal') return '#1e72d0'
      return '#ff748c'
    },
    themeBgColor() {
      if (this.groupType === 'lover') return '#f6b8cc'
      if (this.groupType === 'parent') return '#a8d9b8'
      if (this.groupType === 'child') return '#a8c8f0'
      if (this.groupType === 'other') return '#c4b0e8'
      if (this.groupType === 'personal') return '#a8c8f0'
      return '#e8dfc8'
   }
  },

  watch: {
    $route: {
      handler: async () => {
        this.checkRouteAndLoad()
        await this.loadFootList()
      }
    }
  },

  methods: {
    updateCurrentBg(){
      if (!this.isLogin) {
        this.currentBg = this.randomBg
        return
      }
      if (this.groupType === 'lover') this.currentBg = this.isSquareScreen ? bgLoverSquare : bgLover
      else if (this.groupType === 'parent') this.currentBg = this.isSquareScreen ? bgElderSquare : bgElder
      else if (this.groupType === 'child') this.currentBg = this.isSquareScreen ? bgFamilySquare : bgFamily
      else if (this.groupType === 'other') this.currentBg = this.isSquareScreen ? bgOtherSquare : bgOther
      else if (this.groupType === 'personal') this.currentBg = this.isSquareScreen ? bgPersonalSquare : bgPersonal
      else this.currentBg = this.isSquareScreen ? bgLoverSquare : bgLover
    },
    setMapCanvasMargin() {
      this.$nextTick(()=>{
        if(!this.$refs.titleRef || !this.$refs.bottomRef || !this.$refs.mapBoxRef) return
        const titleH = this.$refs.titleRef.offsetHeight
        const bottomH = this.$refs.bottomRef.offsetHeight
        const gap = 6
        this.$refs.mapBoxRef.style.setProperty('--map-top', `${titleH + gap}px`)
        this.$refs.mapBoxRef.style.setProperty('--map-bottom', `${bottomH + gap}px`)
      })
    },

    async loadFootList() {
      const user = await getCurrentUser();
      if (!user || !user.phone) {
        this.footList = [];
        this.realFootData = []
        return;
      }
      const allFoot = await getFootprintList()
      this.footList = allFoot
      this.realFootData = this.footList
    },
    
    async loadGroupTreeData() {
      const user = await getCurrentUser()
      if (!user || !user.phone) {
        this.groupTreeData = [];
        return;
      }
      const userPhone = user.phone;
      const groupList = await getGroupList()
      const myAllGroups = groupList.filter(g => {
        const isCreator = g.masterAccount === userPhone
        const isJoin = Array.isArray(g.memberList) && g.memberList.some(m => m.userId === userPhone)
        return isCreator || isJoin
      });

      const typeMap = {
        'lover': '恋人伴侣',
        'child': '亲子眷幼',
        'parent': '孝亲伴老',
        'colleague': '同事',
        'friend': '朋友',
        'relative': '亲戚',
        'classmate': '同学'
      }

      const treeObj = {}
      myAllGroups.forEach(g => {
        let typeKey = g.groupType;
        if(g.groupType === 'other') {
          typeKey = g.otherSubType
        }
        if(!treeObj[typeKey]) {
          treeObj[typeKey] = {
            typeKey,
            typeLabel: typeMap[typeKey] || typeKey,
            rawGroupType: g.groupType, 
            groups: []
          }
        }
        treeObj[typeKey].groups.push({
          groupId: g.groupId,
          groupName: g.groupName,
          otherSubType: g.otherSubType
        })
      })
      this.groupTreeData = Object.values(treeObj);
    },

    async checkRouteAndLoad(){
      const viewGroupId = this.$route.query.viewGroupId
      if(viewGroupId){
        const user = await getCurrentUser();
        if (!user || !user.phone) return;
        const groupList = await getGroupList()
        const groupData = groupList.find(g=>g.groupId === viewGroupId)
        if(groupData){
          this.realFootData = this.footList.filter(item=> item.belongGroupId === viewGroupId)
          this.groupType = groupData.groupType || "lover"
        }
      }else{
        await this.loadGroupFootprint()
      }
    },

    async handleLogout() {
      localStorage.removeItem('token')
      localStorage.removeItem('currentUser')
      localStorage.removeItem('user')
      this.isLogin = false
      this.footList = []
      this.footData = []
      this.realFootData = []
      this.groupType = 'lover'
      this.groupTreeData = []
      this.roleName = '个人'
      this.currentView = 'summary'

      const bgPool = this.isSquareScreen 
        ? [bgLoverSquare, bgFamilySquare, bgElderSquare] 
        : [bgLover, bgFamily, bgElder]
      this.randomBg = bgPool[Math.floor(Math.random() * bgPool.length)]
      this.updateCurrentBg()
      this.$router.push('/').catch(() => {})
    },

    handleMapClick(val) {
      if (!this.isLogin) {
        alert('请先登录后再查看足迹')
        return
      }
      const cached = JSON.parse(localStorage.getItem('currentUser') || '{}')
      const currentGroupId = cached.currentGroupId || 'personal'

      let query = {}
      if (typeof val === 'object') {
        query = { ...val }
      } else {
        query = { name: val }
      }
      query.viewGroupId = currentGroupId
      this.$router.push({ path: '/province', query })
    },

    async loadUserFootprint() {
      const user = await getCurrentUser()
      if (!user || !user.phone) {
        this.isLogin = false
        this.footData = []
        this.realFootData = []
        return
      }
      this.isLogin = true
      const gid = user.currentGroupId;
      const allFoot = await getFootprintList()
      const userFoot = allFoot

      if(gid && gid !== 'personal' && gid !== 'summary'){
        const groupList = await getGroupList()
        const groupInfo = groupList.find(g => g.groupId === gid)
        if (!groupInfo) {
          user.currentGroupId = 'personal'
          await setCurrentUser(user)
          this.groupType = 'personal'
          this.realFootData = userFoot.filter(item=> item.belongGroupId === "")
          return
        }
        this.groupType = groupInfo.groupType || 'lover'
        this.realFootData = userFoot.filter(item=> item.belongGroupId === gid)
      }else{
        this.groupType = gid === 'summary' ? this.groupType : 'personal'; 
        this.realFootData = gid === 'summary' ? userFoot : userFoot.filter(item=> item.belongGroupId === "")
        this.updateCurrentBg()
      }
    },

    async loadGroupFootprint(){
      const user = await getCurrentUser()
      if(!user || !user.phone){
        this.footList = []
        this.realFootData = this.footList
        this.$nextTick(()=>{ this.currentView = '' })
        return
      }
      const gid = user.currentGroupId;
      const allFoot = await getFootprintList()
      const userFoot = allFoot

      if(gid === 'personal'){
        this.realFootData = userFoot.filter(item=> item.belongGroupId === "")
      } else if (gid === 'summary') {
        this.realFootData = userFoot
      }else{
        const groupList = await getGroupList()
        const groupData = groupList.find(g=>g.groupId === gid)
        if(groupData){
          this.realFootData = userFoot.filter(item=> item.belongGroupId === gid)
        }else{
          this.realFootData = userFoot.filter(item=> item.belongGroupId === "")
        }
      }
      this.$nextTick(()=>{ this.currentView = '' })
    },

    async refreshRoleInfo(){
      const user = await getCurrentUser()
      if(!user || !user.phone){
        this.roleName = "个人"
        this.groupType = 'personal'
        return
      }
      const gid = user.currentGroupId || null
      if(gid === 'personal'){
        this.roleName = "个人"
        this.groupType = 'personal'
        return
      }
      if(gid === 'summary') { 
        this.roleName = "印记汇总"
        return
      }
      const groupList = await getGroupList()
      const groupData = groupList.find(g=>g.groupId === gid)
      if(groupData){
        this.groupType = groupData.groupType
        let targetKey = groupData.groupType
        if(groupData.groupType === 'other'){ targetKey = groupData.otherSubType }
        const typeMap = {
          'lover': '恋人伴侣', 'child': '亲子眷幼', 'parent': '孝亲伴老',
          'colleague': '同事', 'friend': '朋友', 'relative': '亲戚', 'classmate': '同学'
        }
        this.roleName = groupData.groupName || typeMap[targetKey] || targetKey
      }else{
        this.roleName = "个人"
        this.groupType = 'personal'
      }
    },

    async clickMarkSummary() {
      try{
        this.showRolePopup = false
        this.currentView = 'summary'
        const user = await getCurrentUser()
        user.currentGroupId = 'summary'
        await setCurrentUser(user)
        const allFoot = await getFootprintList()
        const userAllFoot = allFoot
        this.realFootData = userAllFoot
        const validList = userAllFoot.filter(item=> item.province && item.province.trim())
        this.lightProvinceSet = new Set( validList.map(item=>item.province) )
        this.renderMapLight()
        this.$nextTick(()=>this.setMapCanvasMargin())
      }catch(err){
        console.error(err)
        this.$nextTick(()=>this.setMapCanvasMargin())
      }
    },

    renderMapLight() {
      const provinceArr = Array.from(this.lightProvinceSet)
      void provinceArr;
    },

    getViewLabel() {
      if(this.currentView === 'summary'){ return "印记汇总" }
      const found = this.groupTreeData.find( r => r.typeKey === this.currentView )
      return found ? found.typeLabel : ''
    },

    async selectRole(groupId, label, rawGroupType = null, subType = null) {
      const user = await getCurrentUser()
      if(groupId === 'personal'){
        user.currentGroupId = 'personal'
        user.groupType = 'personal'
        await setCurrentUser(user)
        this.groupType = 'personal'
        this.roleName = label
        this.showRolePopup = false
        this.currentView = 'summary'
        await this.loadGroupFootprint()
        this.renderMapLight()
        this.updateCurrentBg()
        return
      }

      user.currentGroupId = groupId
      user.groupType = rawGroupType
      this.groupType = rawGroupType
      this.roleName = label
      await setCurrentUser(user)
      this.showRolePopup = false
      this.currentView = 'summary'
      await this.loadGroupFootprint()
      this.renderMapLight()
      this.updateCurrentBg()
    },

    async openRolePopup() {
      await this.loadGroupTreeData()
      this.showRolePopup = true
    }
  },

  async mounted() {
    this.checkRouteAndLoad()
    await this.loadFootList()
    await this.loadUserFootprint()
    await this.loadGroupTreeData()
    await this.refreshRoleInfo()
    this.updateCurrentBg()

    if (!this.isLogin) {
      const bgPool = this.isSquareScreen 
        ? [bgLoverSquare, bgFamilySquare, bgElderSquare] 
        : [bgLover, bgFamily, bgElder]
      const rndIdx = Math.floor(Math.random() * bgPool.length)
      this.randomBg = bgPool[rndIdx]
      this.updateCurrentBg()
    }
    
    window.addEventListener('orientationchange', this.setMapCanvasMargin)
    this.setMapCanvasMargin()

    const refreshHandler = async () => {
      await this.loadFootList()
      await this.loadGroupTreeData()
    }
    window.addEventListener('refreshFootData', refreshHandler)
    this.refreshHandler = refreshHandler
  },

  beforeDestroy(){
    window.removeEventListener('refreshFootData', this.refreshHandler)
    window.removeEventListener('orientationchange', this.setMapCanvasMargin)
    if(this.resizeTimer) clearTimeout(this.resizeTimer)
    if(this.myChart) {
      this.myChart.dispose()
      this.myChart = null
    }
  },

  async activated(){
    const user = await getCurrentUser()
    await this.refreshRoleInfo()
    await this.loadGroupTreeData()
    this.checkRouteAndLoad()
    this.$nextTick(()=> this.setMapCanvasMargin())
    this.updateCurrentBg()

    if(user.needRefreshMap === 1){
      await this.loadGroupFootprint()
      await this.loadFootList()
      await this.loadGroupTreeData()
      delete user.needRefreshMap
      await setCurrentUser(user)
    }
}
}
</script>

<style scoped>
.home-page {
  width: 100%;
  height: calc(var(--vh,1vh) * 100);
  position: relative;
  overflow: hidden;
}

.footprint-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
}

.layout-inner {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.title-wrap {
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  padding: 15px 20px 4px 20px; 
  top: 15px; 
  display: flex;
  justify-content: flex-start; 
  align-items: center;
   width: fit-content;
}

.title-inner {
  display: inline-block;
  --img-w: clamp(180px, 30vw, 390px); 
  position: relative;
}

.art-title-img {
  width: clamp(180px, 30vw, 390px);
  height: auto;
  display: block;
  margin: 0;
  object-fit: contain; 
}

.map-box {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 2;
  background-color: transparent;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.26), 0 4px 10px rgba(0,0,0,0.14);
}

.legend {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: clamp(20px, 4vh, 60px);
  z-index: 40;
  display: flex;
  justify-content: center;
}

.bottom-action-wrap {
  position: absolute;
  left: 0;
  width: fit-content;
  bottom: 15px;
  z-index: 50;
  padding: 12px 16px;
  padding-bottom: calc(12px + var(--safe-bottom));
}

.role-text-line {
  text-align: left;
  padding-left: clamp(50px, 9.5vw, 125px); 
  color: #296cd8;
  font-size: clamp(9px, 1.2vw, 13px);
  margin-bottom: 12px;
  margin-top: 0;
  width: 100%;
  display: block;
  text-shadow: 1px 1px 0 #fff, -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff;
}

.btn-bottom {
  display: flex;
  justify-content: flex-start;
  padding-left: clamp(20px, 5vw, 80px);
  gap: clamp(8px, 1vw, 16px); 
  width: fit-content;
  min-height: 0;
  align-items: center;
}

.login-btn {
  min-width: 0;
  padding: clamp(5px, 0.8vw, 4px) clamp(7px, 1vw, 10px); 
  border: none;
  border-radius: 999px;
  color: #fff;
  font-size: clamp(9px, 1.2vw, 12px); 
  font-family: "楷体", "行楷", "Microsoft YaHei", cursive;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255,255,255,0.4);
  text-align: center;
}

.theme-lover .login-btn { background: linear-gradient(145deg, #ff7a9a, #d6486b); }
.theme-family .login-btn { background: linear-gradient(145deg, #55a8ff, #2b7cdf); }
.theme-elder .login-btn { background: linear-gradient(145deg, #49c783, #239957); }
.theme-other .login-btn { background: linear-gradient(145deg, #a88cf0, #8866dd); }
.theme-personal .login-btn { background: linear-gradient(145deg, #53a7ff, #1e72d0); }

.login-outline {
  background: rgba(255, 255, 255, 0.72) !important;
  color: #333 !important;
  border: 1.8px solid;
}
.theme-lover .login-outline { border-color: #d6486b; }
.theme-family .login-outline { border-color: #2b7cdf; }
.theme-elder .login-outline { border-color: #239957; }
.theme-other .login-outline { border-color: #8866dd; }
.theme-personal .login-outline { border-color: #1e72d0; }

.login-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255,255,255,0.4);
}

@keyframes shakeTip {
  0% { transform: translateX(0); } 20% { transform: translateX(-2px); } 40% { transform: translateX(2px); }
  60% { transform: translateX(-2px); } 80% { transform: translateX(2px); } 100% { transform: translateX(0); }
}

.login-outline { animation: shakeTip 0.6s ease-in-out 3s infinite; }
.login-outline:hover { animation: none; }

.theme-lover .visited { background: linear-gradient(145deg, #ff92a8, #d6486b); }
.theme-family .visited { background: linear-gradient(145deg, #6cb8ff, #2b7cdf); }
.theme-elder .visited { background: linear-gradient(145deg, #5fd08b, #239957); }
.theme-other .visited { background: linear-gradient(145deg, #a88cf0, #8866dd); }
.theme-personal .visited { background: linear-gradient(145deg, #53a7ff, #1e72d0); }
.unvisit { background: #e6e6e6; }

.leg-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #555;
  font-size: 14px;
}

.dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.9), 0 2px 6px rgba(0, 0, 0, 0.15);
}

.popup-mask {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  z-index: 999;
  display: flex; align-items: flex-end; justify-content: center;
  padding-bottom: 5vh; 
  box-sizing: border-box;
}

.popup-panel {
  width: clamp(300px, 53vw, 420px); margin-bottom: 16px; max-height: 80vh;
  display: flex; flex-direction: column;
  background-image: url("~@/assets/subtle.webp"); background-repeat: repeat; background-size: 600px auto; background-position: center;
  background-color: rgba(247, 243, 233, 0.55);
  border-radius: 22px !important; 
  overflow: hidden !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  animation: popupUp 0.25s ease-out; border: none; outline: none;
}


@keyframes popupUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

.popup-title { text-align: center; padding: clamp(10px, 2vh, 16px) 0 clamp(6px, 1vh, 10px);
 font-size: clamp(13px, 1.3vw, 16px) !important; 
 font-weight: 700; color: #d14a4a; user-select: none; }
.role-list-wrap { overflow-y: auto; flex: 1;  padding: 0 clamp(10px, 2vw, 16px) clamp(10px, 2vh, 16px); scrollbar-width: none; }
.role-list-wrap::-webkit-scrollbar { display: none; }
.pop-type-row {
  display: flex; align-items: center; padding: 4px clamp(8px, 1vw, 12px); 
  gap: clamp(3px, 0.5vw, 5px); cursor: pointer;
  transition: background-color 0.15s; border-bottom: 1px solid rgba(226, 221, 212, 0.6);
}
.pop-type-row:last-child { border-bottom: none; }
.pop-type-row:hover { background-color: rgba(160, 110, 70, 0.10); }
.type-dot { width: clamp(6px, 0.8vw, 8px); height: clamp(6px, 0.8vw, 8px); border-radius: 50%; background: #f56c6c; flex-shrink: 0; box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.15); }
.type-title-text { font-size: clamp(12px, 1.2vw, 14px); font-weight: 600; color: #2b2b2b; white-space: nowrap; width: clamp(52px, 4.7vw, 60px);  text-align: justify; text-align-last: justify; margin-right: clamp(6px, 1vw, 12px); flex-shrink: 0; }
.type-inline-groups { display: flex; gap: clamp(4px, 0.8vw, 6px);flex-wrap: wrap; flex: 1;min-width: 0; }
.pop-inline-child-item {
padding: 1px 4px; cursor: pointer; background: #ecf5ff; color: #409eff; border-radius: 4px; white-space: nowrap; font-size: clamp(10px, 1.1vw, 12px); border: 1px solid #d9ecff; transition: all 0.2s;font-weight: 600;
}
.pop-inline-child-item:hover { background: #409eff; color: #fff; border-color: #409eff; transform: translateY(-1px); box-shadow: 0 3px 8px rgba(64, 158, 255, 0.25); }

:deep(.login-wrap) {
  position: absolute;
  left: clamp(80px, 16vw, 200px); 
  bottom: 15px;
  width: fit-content;
  z-index: 50;
  padding: 8px 12px;
  padding-bottom: calc(12px + var(--safe-bottom));
}

:deep(button.login-btn.login-outline) { border: none; outline: none; }

.footprint-bg-img {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; object-position: center; z-index: 0; display: block; opacity: 0; transition: opacity 0.4s ease;
}
.footprint-bg-img.bg-loaded { opacity: 1; }

.help-icon {
  width: clamp(24px, 3vw, 36px); 
  height: clamp(24px, 3vw, 36px);
  display: flex; align-items: center; justify-content: center;
  font-size: clamp(12px, 1.5vw, 16px);
  flex-shrink: 0;
  margin-right: 1px;
  cursor: pointer;
  z-index: 99;
  animation: shake 4s infinite ease-in-out;
  transition: all 0.2s ease;
  background: transparent;
}

.help-icon:hover { transform: scale(1.12); animation: none; }

@keyframes shake {
  0% { transform: translateX(0); } 48% { transform: translateX(0); } 50% { transform: translateX(-3px); } 52% { transform: translateX(3px); }
  54% { transform: translateX(-2px); } 56% { transform: translateX(2px); } 58% { transform: translateX(0); } 100% { transform: translateX(0); }
}

.copyright-box {
  position: absolute;
  right: 20px;       
  bottom: 2px;      
  padding-bottom: var(--safe-bottom);
  
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap; 
  z-index: 60;
  
  font-size: clamp(7px, 0.8vw, 9px);
  color: rgba(220, 220, 220, 0.9); 
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9); 
}

.copyright-box a {
  color: inherit;
  text-decoration: none;
}
.copyright-box a:hover {
  text-decoration: underline;
  color: #fff;
}

</style>